import groupBy from 'lodash/groupBy'
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { RouteComponentProps, useParams } from 'react-router-dom'
import { H, Wrapper } from '@farewill/ui'
import { shallowEqual, useSelector } from 'react-redux'

import ENV from 'config/environment'
import AircallEventComponent from 'components/aircall-event'
import EightxEightEventComponent from 'components/eight-x-eight-event'
import LeadEventItem from 'components/lead-event'
import { useTasksContext } from 'components/tasks-panel/context'
import useLeadEvents from 'hooks/useLeadEvents'
import useApi from 'lib/effects/api'
import { AircallEvent, AircallEventAttributes } from 'lib/models/aircall-event'
import {
  EightxEightEvent,
  EightxEightEventAttributes,
  EIGHT_X_EIGHT_REQUEST_TIMEOUT_MS,
} from 'lib/models/eight-x-eight-event'
import { CaseStatusTransition } from 'lib/models/case-status-transition'
import { Comment } from 'lib/models/comment'
import { Lead } from 'lib/models/lead'
import { Event } from 'lib/models/event'
import { Invoice } from 'lib/models/invoice'
import { LeadEvent } from 'lib/models/lead-event'
import { Task as TaskItem } from 'lib/models/task'
import { scrollToDataId } from 'lib/window/scroll'
import {
  ErrorsProvider,
  useErrorsContext,
} from 'routes/probate-case/components/errors/context'
import { PRODUCTS, FAREWILL_PRODUCT_IDS } from 'utils/enums'

import CaseCommentForm from './case-comment-form'
import CommentThread from './comment-thread'
import { ActivityProvider, useActivityContext } from './context'
import EightXEightWarning from './eight-x-eight-warning'
import Error from './error'
import EventComponent from './event'
import FilterActivity from './filter-activity'
import {
  Activity as ActivityType,
  getCaseCommentThreads,
  getTaskCommentThreads,
  report8x8FetchError,
  sortByActivityDate,
  useEvents,
  useProbateCaseEvents,
} from './helpers'
import Payment from './payment'
import ScheduledEmailsSummary from './scheduled-emails-summary'
import CaseStatusTransitionComponent from './status-transition'
import Task from './task'
import TaskEmail from './task-email'
import AIGeneratedNotesSummary from './ai-summary'
import { ExternalEmail } from './external-email'
import { ExternalEmail as ExternalEmailType } from 'lib/models/external-email'
import { PRODUCTS_WITH_TASKS } from 'components/tasks-panel/constants'

type RouteProps = RouteComponentProps<{ id: string }>

type Props = RouteProps & {
  leadId: number | null
  location: string
  product: string
  showEmailFilter?: boolean
  showCallsFilter?: boolean
  showPaymentsFilter?: boolean
  showOthersFilter?: boolean
  /**
   * Path to route that contains tasks. For Probate, tasks are viewed on their
   * own page/route, whereas for other products tasks are shown in a sidebar
   * that exists on multiple case page tabs.
   */
  tasksRoutePath?: string
}

const Activity = ({
  leadId,
  location,
  showEmailFilter,
  showCallsFilter,
  showPaymentsFilter,
  showOthersFilter,
  product,
  tasksRoutePath,
}: Props): ReactElement => {
  const {
    comments,
    fetchComments,
    isFetchingComments,
    emails,
    fetchEmails,
    externalEmails,
    fetchExternalEmails,
    invoices,
    fetchInvoices,
    statusTransitions,
    fetchStatusTransitions,
  } = useActivityContext()
  const lead = useSelector(
    (state: { lead: Lead }) => state.lead.attributes && state.lead,
    shallowEqual
  )
  // Some products (e.g. Funeral plans) don't use tasks and are not wrapped in
  // a TasksProvider.
  const { tasks } = useTasksContext({
    enabled: (PRODUCTS_WITH_TASKS as readonly string[]).includes(product),
  })
  const completedTasks = ((tasks as TaskItem[]) || []).filter(
    ({ attributes }) => !!attributes.completedAt
  )

  const { errors, fetchErrors } = useErrorsContext()
  const { id: productId } = useParams<{ id: string }>()
  const probateCaseEvents = useProbateCaseEvents({
    product,
    productId,
  })

  const filterDefault = product === PRODUCTS.FUNERAL ? false : true
  const emailsFilterDefault = product === PRODUCTS.PROBATE

  const [activityFilters, setActivityFilters] = useState({
    comments: true,
    tasks: filterDefault,
    emails: emailsFilterDefault,
    calls: true,
    payments: filterDefault,
    others: true,
  })

  const queryKey =
    FAREWILL_PRODUCT_IDS[product as keyof typeof FAREWILL_PRODUCT_IDS]

  useEffect(() => {
    fetchComments({ queryParams: { [queryKey]: productId } })
  }, [fetchComments, productId, queryKey])

  const taskCompletedAtDates = (tasks as TaskItem[])
    .map((task) => task.attributes.completedAt)
    .join(' ')

  useEffect(() => {
    if (product === PRODUCTS.PROBATE) {
      fetchEmails({ queryParams: { 'filter[probateCaseId]': productId } })
    }
  }, [fetchEmails, productId, taskCompletedAtDates, product])

  useEffect(() => {
    if (product === PRODUCTS.PROBATE) {
      fetchExternalEmails(productId)
    }
  }, [fetchExternalEmails, productId, product])

  useEffect(() => {
    if (product === PRODUCTS.PROBATE) {
      fetchErrors()
    }
  }, [fetchErrors, product])

  useEffect(() => {
    if (product === PRODUCTS.WILL) {
      fetchStatusTransitions({
        queryParams: { 'filter[willCaseId]': productId },
      })
    }
  }, [fetchStatusTransitions, product, productId])

  useEffect(() => {
    if (product === PRODUCTS.FUNERAL) {
      fetchInvoices({ queryParams: { 'filter[funeralId]': productId } })
    }
  }, [fetchInvoices, productId, product])

  const events = useEvents({
    product,
    params: `funeralPlanId=${productId}`,
  })

  const { leadEvents, fetchLeadEvents } = useLeadEvents(leadId as number)
  const [{ data: aircallEventsData }, fetchAircallEventsForCustomer] =
    useApi<AircallEventAttributes[]>()
  const [
    {
      data: eightxEightEventsData,
      meta: eightxEightMeta,
      isLoading: isLoadingEightxEightEvents,
    },
    fetch8x8EventsForCustomer,
  ] = useApi<EightxEightEventAttributes[]>()
  // The server flags 8x8 failures in the response meta, but a timeout or
  // network error never gets a response, so track those separately
  const [eightXEightRequestFailed, setEightXEightRequestFailed] =
    useState(false)
  // Only the latest 8x8 request may show the warning, so a request for a
  // previous lead that fails late is ignored
  const latest8x8Request = useRef(0)

  const load8x8EventsForCustomer = useCallback(() => {
    latest8x8Request.current += 1
    const request = latest8x8Request.current
    const eightXEightStartedAt = Date.now()
    setEightXEightRequestFailed(false)
    fetch8x8EventsForCustomer({
      url: `/api/8x8/get-events-for-customer?leadId=${leadId}`,
      timeout: EIGHT_X_EIGHT_REQUEST_TIMEOUT_MS,
      // EightXEightWarning explains the failure and offers Try again
      silent: true,
    }).catch((error) => {
      if (request === latest8x8Request.current) {
        setEightXEightRequestFailed(true)
      }
      report8x8FetchError(error, {
        leadId,
        startedAt: eightXEightStartedAt,
      })
    })
  }, [fetch8x8EventsForCustomer, leadId])

  useEffect(() => {
    if (leadId) {
      fetchLeadEvents()
      fetchAircallEventsForCustomer({
        url: `/api/aircall/get-aircall-events-for-customer?leadId=${leadId}`,
      })
      load8x8EventsForCustomer()
    }
  }, [
    fetchLeadEvents,
    leadId,
    fetchAircallEventsForCustomer,
    load8x8EventsForCustomer,
  ])

  const groupedComments = groupBy(comments, 'attributes.taskId')
  let aircallEvents: AircallEvent[] = []
  if (aircallEventsData && aircallEventsData.length > 0) {
    aircallEvents = aircallEventsData.map((event) => ({
      id: event.id,
      type: 'aircall',
      attributes: event,
    }))
  }
  let eightxEightEvents: EightxEightEvent[] = []
  if (eightxEightEventsData && eightxEightEventsData.length > 0) {
    eightxEightEvents = eightxEightEventsData.map((event) => ({
      id: event.id,
      type: '8x8',
      attributes: event,
    }))
  }
  let activities: ActivityType[] = []

  if (activityFilters.others) {
    activities = [
      ...(statusTransitions as CaseStatusTransition[]),
      ...(events as Event[]),
      ...(probateCaseEvents as Event[]),
      ...leadEvents
        .filter((event) => event.attributes.type !== 'call_made')
        .map((event) => ({ ...event, type: 'lead_events' })),
    ] as unknown as ActivityType[]
  }

  if (activityFilters.tasks) {
    activities = [...activities, ...completedTasks, ...errors]
  }

  if (activityFilters.payments) {
    activities = [...activities, ...invoices]
  }

  if (activityFilters.comments) {
    activities = [
      ...activities,
      ...getCaseCommentThreads({ groupedComments, isCall: false }),
      ...getTaskCommentThreads(groupedComments),
    ]
  }

  if (activityFilters.emails) {
    activities = [...activities, ...emails, ...externalEmails]
  }

  if (activityFilters.calls) {
    activities = [
      ...activities,
      ...getCaseCommentThreads({ groupedComments, isCall: true }),
      ...aircallEvents,
      ...eightxEightEvents,
      ...leadEvents
        .filter((event) => event.attributes.type === 'call_made')
        .map((event) => ({ ...event, type: 'lead_events' })),
    ]
  }

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => scrollToDataId(location.hash.replace('#', '')))
    }
  }, [location.hash, isFetchingComments])

  return (
    <Wrapper>
      {ENV.FF_AI_NOTES_ENABLED && (
        <AIGeneratedNotesSummary lead={lead} leadEvents={leadEvents} />
      )}
      <H size="M">Activity</H>
      <ScheduledEmailsSummary />
      {product !== PRODUCTS.FUNERAL_PLAN && (
        <FilterActivity
          activityFilters={activityFilters}
          setActivityFilters={setActivityFilters}
          showEmailFilter={showEmailFilter}
          showCallsFilter={showCallsFilter}
          showPaymentsFilter={showPaymentsFilter}
          showOthersFilter={showOthersFilter}
        />
      )}
      <CaseCommentForm product={product} />
      {!isLoadingEightxEightEvents &&
        (eightXEightRequestFailed || eightxEightMeta?.eightXEightFailed) && (
          <EightXEightWarning onRetry={load8x8EventsForCustomer} />
        )}
      {[...activities].sort(sortByActivityDate).map((activity) => {
        if (activity.type === 'emails') {
          return (
            <TaskEmail email={activity} key={`task-email-${activity.id}`} />
          )
        }
        if (activity.type === 'external_emails') {
          return (
            <ExternalEmail
              email={activity as unknown as ExternalEmailType}
              key={`external-email-${activity.id}`}
            />
          )
        }
        if (activity.type === 'invoices') {
          return (
            <Payment
              invoice={activity as Invoice}
              key={`payment-${activity.id}`}
            />
          )
        }
        if (activity.type === 'tasks') {
          return (
            <Task
              task={activity}
              tasksRoutePath={tasksRoutePath}
              key={`task-${activity.id}`}
            />
          )
        }
        if (activity.type === 'probate_case_errors') {
          return <Error error={activity} key={`error-${activity.id}`} />
        }
        if (activity.type === 'case_status_transitions') {
          return (
            <CaseStatusTransitionComponent
              caseStatusTransition={activity as CaseStatusTransition}
              key={`case-status-transition-${activity.id}`}
            />
          )
        }
        if (activity.type === 'events') {
          return (
            <EventComponent
              event={activity as Event}
              key={`event-${activity.id}`}
            />
          )
        }
        if (activity.type === 'lead_events') {
          return (
            <LeadEventItem
              event={activity as LeadEvent}
              key={`event-${activity.id}`}
            />
          )
        }
        if (activity.type === 'aircall') {
          return (
            <AircallEventComponent
              event={activity.attributes as AircallEventAttributes}
              leadId={leadId ?? undefined}
              key={`event-${activity.id}`}
            />
          )
        }
        if (activity.type === '8x8') {
          return (
            <EightxEightEventComponent
              event={activity.attributes as EightxEightEventAttributes}
              leadId={leadId ?? undefined}
              key={`event-${activity.id}`}
            />
          )
        }
        return (
          <CommentThread
            comment={activity as Comment}
            key={`comment-${activity.id}`}
            enableCommenting
            queryParams={{ [queryKey]: productId }}
            tasksRoutePath={tasksRoutePath}
          />
        )
      })}
    </Wrapper>
  )
}

const ActivityWithProvider = (props: Props): React.ReactElement => (
  <ActivityProvider>
    <ErrorsProvider probateCaseId={props.match.params.id}>
      <Activity {...props} />
    </ErrorsProvider>
  </ActivityProvider>
)

export default ActivityWithProvider
